import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Radio, 
  Clock, 
  TrendingUp, 
  TrendingDown,
  Activity,
  Zap,
  Users,
  RefreshCw
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

interface LiveMatch {
  id: string;
  sport: string;
  league: string;
  home_team: string;
  away_team: string;
  home_score: number;
  away_score: number;
  match_status: string;
  start_time: string;
  current_minute: number | null;
  live_odds: Record<string, number>;
  match_events: any[];
  is_live: boolean;
}

export function LiveMatchTracker() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [matches, setMatches] = useState<LiveMatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [onlineUsers, setOnlineUsers] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    fetchMatches();
    const subscription = setupRealtimeSubscription();
    
    let presenceCleanup: (() => void) | undefined;
    trackPresence().then(cleanup => { presenceCleanup = cleanup; });

    // Auto-refresh every 30 seconds for live updates
    const refreshInterval = setInterval(() => {
      fetchMatches();
    }, 30000);

    return () => {
      subscription();
      presenceCleanup?.();
      clearInterval(refreshInterval);
    };
  }, []);

  const fetchMatches = async () => {
    try {
      const { data, error } = await supabase
        .from('live_matches')
        .select('*')
        .order('start_time', { ascending: true });

      if (error) throw error;
      setMatches((data || []).map(m => ({
        ...m,
        live_odds: m.live_odds as Record<string, number>,
        match_events: m.match_events as any[]
      })));
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel('live-matches-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_matches'
        },
        async (payload) => {
          console.log('Match update received:', payload);
          
          if (payload.eventType === 'INSERT') {
            setMatches(prev => [...prev, payload.new as LiveMatch]);
          } else if (payload.eventType === 'UPDATE') {
            const updatedMatch = payload.new as LiveMatch;
            
            setMatches(prev => prev.map(match => 
              match.id === updatedMatch.id ? updatedMatch : match
            ));
            
            // Auto-recalculate win probabilities for live matches
            if (updatedMatch.is_live && updatedMatch.match_events) {
              const lastEvent = updatedMatch.match_events[updatedMatch.match_events.length - 1];
              
              // Show toast for important events
              if (lastEvent?.type === 'goal') {
                toast({
                  title: "⚽ GOAL!",
                  description: `${updatedMatch.home_team} ${updatedMatch.home_score} - ${updatedMatch.away_score} ${updatedMatch.away_team}`,
                });
              } else if (lastEvent?.type === 'red_card') {
                toast({
                  title: "🟥 RED CARD!",
                  description: `${updatedMatch.home_team} vs ${updatedMatch.away_team}`,
                  variant: "destructive"
                });
              }
              
              // Trigger probability recalculation via edge function
              try {
                await supabase.functions.invoke('refresh-ai-predictions', {
                  body: { match_id: updatedMatch.id }
                });
              } catch (error) {
                console.error('Failed to refresh predictions:', error);
              }
            }
          } else if (payload.eventType === 'DELETE') {
            setMatches(prev => prev.filter(match => match.id !== payload.old.id));
          }
          
          setLastUpdated(new Date());
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const trackPresence = async () => {
    if (!user) return;

    const presenceChannel = supabase.channel('online-users');

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = presenceChannel.presenceState();
        setOnlineUsers(Object.keys(state).length);
      })
      .on('presence', { event: 'join' }, ({ key, newPresences }) => {
        console.log('User joined:', key, newPresences);
      })
      .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        console.log('User left:', key, leftPresences);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await presenceChannel.track({
            user_id: user.id,
            online_at: new Date().toISOString(),
          });
        }
      });

    return () => {
      supabase.removeChannel(presenceChannel);
    };
  };

  const getSportIcon = (sport: string) => {
    const icons: Record<string, string> = {
      Football: '⚽',
      Basketball: '🏀',
      Tennis: '🎾',
      Baseball: '⚾'
    };
    return icons[sport] || '🏆';
  };

  const getMatchStatusColor = (status: string) => {
    switch (status) {
      case 'live':
        return 'bg-red-500 text-white animate-pulse';
      case 'upcoming':
        return 'bg-blue-500/20 text-blue-700 border-blue-500/30';
      case 'finished':
        return 'bg-gray-500/20 text-gray-700 border-gray-500/30';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  // Filter matches by status
  const now = new Date();
  const upcomingMatches = matches.filter(m => new Date(m.start_time) > now);
  const liveMatches = matches.filter(m => 
    new Date(m.start_time) <= now && 
    new Date(m.start_time) > new Date(now.getTime() - 2 * 60 * 60 * 1000)
  );
  const finishedMatches = matches.filter(m => 
    new Date(m.start_time) < new Date(now.getTime() - 2 * 60 * 60 * 1000)
  );

  const renderMatchCard = (match: LiveMatch) => (
    <Card 
      key={match.id} 
      className={`hover:shadow-lg transition-all duration-300 ${
        match.is_live ? 'border-red-500/30 bg-gradient-to-r from-red-500/5 to-orange-500/5' : ''
      }`}
    >
      <CardContent className="p-6">
        <div className="space-y-4">
          {/* Match Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{getSportIcon(match.sport)}</span>
              <div>
                <div className="font-semibold">{match.league}</div>
                <div className="text-sm text-muted-foreground">
                  {new Date(match.start_time).toLocaleString()}
                </div>
              </div>
            </div>
            <Badge className={getMatchStatusColor(match.match_status)}>
              {match.is_live && match.current_minute && (
                <Clock className="h-3 w-3 mr-1" />
              )}
              {match.is_live 
                ? `${match.current_minute}'` 
                : match.match_status.toUpperCase()}
            </Badge>
          </div>

          {/* Score Display */}
          <div className="flex items-center justify-between py-4 px-6 bg-muted/50 rounded-lg">
            <div className="flex-1 text-center">
              <div className="font-bold text-xl mb-1">{match.home_team}</div>
              {match.is_live && (
                <div className="text-4xl font-bold text-primary">
                  {match.home_score}
                </div>
              )}
            </div>
            <div className="px-4 text-muted-foreground font-semibold">
              {match.is_live ? 'VS' : '-'}
            </div>
            <div className="flex-1 text-center">
              <div className="font-bold text-xl mb-1">{match.away_team}</div>
              {match.is_live && (
                <div className="text-4xl font-bold text-primary">
                  {match.away_score}
                </div>
              )}
            </div>
          </div>

          {/* Live Odds */}
          {Object.keys(match.live_odds).length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(match.live_odds).slice(0, 3).map(([key, value]) => (
                <Button 
                  key={key}
                  variant="outline" 
                  className="flex flex-col gap-1 h-auto py-3 hover:bg-primary/10 hover:border-primary group"
                >
                  <span className="text-xs text-muted-foreground group-hover:text-foreground">
                    {key.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className="text-lg font-bold flex items-center gap-1">
                    {value.toFixed(2)}
                    {value > 2 ? (
                      <TrendingUp className="h-4 w-4 text-green-500" />
                    ) : (
                      <TrendingDown className="h-4 w-4 text-red-500" />
                    )}
                  </span>
                </Button>
              ))}
            </div>
          )}

          {/* Action Button */}
          <Button 
            className="w-full" 
            variant={match.is_live ? "default" : "outline"}
          >
            <Zap className="h-4 w-4 mr-2" />
            {match.is_live ? 'Bet Live Now' : 'View Odds'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="p-6">
              <div className="h-32 bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <Card className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border-red-500/20">
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <CardTitle className="flex items-center gap-2">
              <Radio className="h-6 w-6 text-red-500 animate-pulse" />
              Live Matches
            </CardTitle>
            <div className="flex items-center gap-4 flex-wrap">
              <Badge variant="outline" className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-green-500" />
                {liveMatches.length} Live
              </Badge>
              <Badge variant="outline" className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-500" />
                {onlineUsers} Online
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchMatches}
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <div className="text-sm text-muted-foreground">
                Last Updated: {lastUpdated.toLocaleTimeString()}
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabs for Upcoming/Live/Finished */}
      <Tabs defaultValue="live" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upcoming" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Upcoming ({upcomingMatches.length})
          </TabsTrigger>
          <TabsTrigger value="live" className="flex items-center gap-2">
            <Radio className="h-4 w-4" />
            Live ({liveMatches.length})
          </TabsTrigger>
          <TabsTrigger value="finished" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            Finished ({finishedMatches.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="space-y-4">
          {upcomingMatches.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No upcoming matches</p>
              </CardContent>
            </Card>
          ) : (
            upcomingMatches.map(renderMatchCard)
          )}
        </TabsContent>

        <TabsContent value="live" className="space-y-4">
          {liveMatches.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Radio className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No live matches at the moment</p>
                <p className="text-sm text-muted-foreground">Check back soon for live action!</p>
              </CardContent>
            </Card>
          ) : (
            liveMatches.map(renderMatchCard)
          )}
        </TabsContent>

        <TabsContent value="finished" className="space-y-4">
          {finishedMatches.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Activity className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No finished matches</p>
              </CardContent>
            </Card>
          ) : (
            finishedMatches.map(renderMatchCard)
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
